import { fr } from "date-fns/locale";
import { CalendarIcon, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { APP_CONFIG } from "@/shared/configs/app.config";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/Button";
import { Calendar } from "@/shared/ui/Calendar";
import { Input } from "@/shared/ui/Input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/Popover";
import { DateTimeActions } from "./DateTimeActions";
import { SegmentedDateInput } from "./SegmentedDateInput";
import { TimeSelector } from "./TimeSelector";
import {
  formatTime,
  formatDisplayDate,
  getTodayMidnight,
  createDateTime,
  isValidFutureTime,
  getMinTimeForDate,
} from "./utils";

type DateTimePickerProps = {
  value: string | null
  onValueChange: (value?: string) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  showTime?: boolean
  showActions?: boolean
  allowPastDates?: boolean
  minDate?: string | null
  maxDate?: string | null
  minuteStep?: number
  isDateDisabled?: (date: Date) => boolean
  minHour?: number
  maxHour?: number
  showTimeSelector?: boolean
  ariaLabel?: string
  leadingLabel?: string
  showTodayShortcut?: boolean
};

const formatToDisplay = (value: string | null): string => {
  if (!value) return "";
  const dateOnly = value.split("T")[0];
  const parts = dateOnly.split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

const parseDisplayDate = (input: string): Date | null => {
  const trimmed = input.trim();
  const match = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  if (month < 1 || month > 12 || day < 1 || year < 1900 || year > 2100) return null;

  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
};

const formatDateToYmd = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const DateTimePicker = ({
  value,
  onValueChange,
  disabled = false,
  placeholder = "Sélectionner une date et heure",
  className,
  showTime = true,
  showActions = true,
  allowPastDates = false,
  minDate = null,
  maxDate = null,
  minuteStep = 5,
  isDateDisabled,
  minHour,
  maxHour,
  showTimeSelector = true,
  ariaLabel,
  leadingLabel,
  showTodayShortcut = false,
}: DateTimePickerProps) => {
  const [open, setOpen] = useState(false);
  const [openedViaInput, setOpenedViaInput] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value ? new Date(value) : undefined,
  );
  const [timeValue, setTimeValue] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>(() => formatToDisplay(value));
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    value ? new Date(value) : new Date(),
  );
  const clearValue = () => {
    setSelectedDate(undefined);
    setTimeValue("");
    setInputValue("");
    onValueChange(undefined);
  };

  /* The parent is told once per empty streak that the value is empty — some
   * forms rely on it to turn their initial null into undefined. StrictMode
   * replays this effect with the stale null after a form reset: notifying again
   * would wipe the value just loaded (empty dates on a games calendar). */
  const emptyNotifiedRef = useRef(false);

  useEffect(() => {
    if (value !== null) {
      emptyNotifiedRef.current = false;
      const date = new Date(value);
      setSelectedDate(date);
      setCalendarMonth(date);
      setTimeValue(formatTime(date));
      if (!showTime) {
        setInputValue(formatToDisplay(value));
      }
    }
    else if (emptyNotifiedRef.current) {
      setSelectedDate(undefined);
      setTimeValue("");
      setInputValue("");
    }
    else {
      emptyNotifiedRef.current = true;
      clearValue();
    }
  }, [value]);

  const clearValueAndClose = () => {
    clearValue();
    setOpen(false);
  };

  const isDateValid = (date: Date): boolean => {
    if (!allowPastDates && date < getTodayMidnight()) return false;
    if (minDate) {
      const minDateObj = new Date(minDate);
      minDateObj.setHours(0, 0, 0, 0);
      if (date < minDateObj) return false;
    }
    if (maxDate) {
      const maxDateObj = new Date(maxDate);
      maxDateObj.setHours(23, 59, 59, 999);
      if (date > maxDateObj) return false;
    }
    if (isDateDisabled?.(date)) return false;
    return true;
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      clearValue();
      return;
    }

    if (!allowPastDates && date < getTodayMidnight()) {
      return;
    }

    setSelectedDate(date);

    if (!showTime) {
      const ymd = formatDateToYmd(date);
      setInputValue(formatToDisplay(ymd));
      onValueChange(ymd);
      setOpen(false);
      return;
    }

    updateDateTime(date, timeValue);
  };

  const handleInputBlur = () => {
    const isEmpty = !inputValue.trim() || inputValue === "00/00/0000";
    if (isEmpty) {
      if (value) {
        clearValue();
      }
      return;
    }

    const parsed = parseDisplayDate(inputValue);
    if (!parsed || !isDateValid(parsed)) {
      setInputValue(formatToDisplay(value));
      return;
    }

    const ymd = formatDateToYmd(parsed);
    setSelectedDate(parsed);
    setCalendarMonth(parsed);
    onValueChange(ymd);
  };

  const handleTimeChange = (time: string) => {
    setTimeValue(time);
    if (selectedDate) {
      updateDateTime(selectedDate, time);
    }
  };

  const updateDateTime = (date: Date, time: string) => {
    if (!allowPastDates && !isValidFutureTime(date, time)) return;

    const dateTime = createDateTime(date, time);
    if (!dateTime) return;
    onValueChange(dateTime.toISOString());
  };

  const getDisplayText = () => {
    if (!selectedDate) return placeholder;

    const date = formatDisplayDate(selectedDate);

    if (!showTime) return date;

    return timeValue ? `${date} à ${timeValue}` : date;
  };

  const minTime = selectedDate ? getMinTimeForDate(selectedDate) : "00:00";

  const isTodayDisabled = (() => {
    const today = getTodayMidnight();
    if (minDate) {
      const minDateObj = new Date(minDate);
      minDateObj.setHours(0, 0, 0, 0);
      if (today < minDateObj) return true;
    }
    if (maxDate) {
      const maxDateObj = new Date(maxDate);
      maxDateObj.setHours(23, 59, 59, 999);
      if (today > maxDateObj) return true;
    }
    if (isDateDisabled?.(today)) return true;
    return false;
  })();

  const handleTodayClick = () => {
    if (isTodayDisabled) return;
    handleDateSelect(getTodayMidnight());
  };

  const getDisabledDates = (date: Date): boolean => {
    if (!allowPastDates && date < getTodayMidnight()) {
      return true;
    }
    if (minDate) {
      const minDateObj = new Date(minDate);
      minDateObj.setHours(0, 0, 0, 0);
      if (date < minDateObj) {
        return true;
      }
    }
    if (maxDate) {
      const maxDateObj = new Date(maxDate);
      maxDateObj.setHours(23, 59, 59, 999);
      if (date > maxDateObj) {
        return true;
      }
    }
    if (isDateDisabled?.(date)) {
      return true;
    }
    return false;
  };

  if (!showTime) {
    return (
      <Popover
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) setOpenedViaInput(false);
        }}
      >
        <div
          ref={wrapperRef}
          data-slot="date-picker"
          className={cn(
            "flex items-center rounded-md border border-input bg-background",
            open && "ring-ring/50 ring-[3px] border-ring",
            disabled && "opacity-50 cursor-not-allowed",
            className,
          )}
        >
          {leadingLabel && (
            <span className="pl-3 shrink-0 truncate max-w-fit text-sm text-muted-foreground">
              {leadingLabel}
            </span>
          )}
          <SegmentedDateInput
            value={inputValue}
            onChange={setInputValue}
            onCommit={handleInputBlur}
            onFocus={() => {
              if (!disabled) {
                setOpenedViaInput(true);
                setOpen(true);
              }
            }}
            disabled={disabled}
            ariaLabel={ariaLabel}
            className="border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          {showTodayShortcut && (
            <button
              type="button"
              aria-label="Choisir aujourd’hui"
              disabled={disabled || isTodayDisabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={handleTodayClick}
              className="shrink-0 cursor-pointer px-2 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              Aujourd’hui
            </button>
          )}
          {!!selectedDate && !disabled && (
            <button
              type="button"
              title="Effacer la date"
              onMouseDown={(e) => e.preventDefault()}
              onClick={clearValue}
              className="p-1 shrink-0 rounded hover:bg-slate-100"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="date-picker-toggle h-7 w-7 p-0 mr-1 shrink-0 rounded-sm bg-white"
              disabled={disabled}
            >
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
        </div>
        <PopoverContent
          className="w-auto p-0 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto"
          align="end"
          side="bottom"
          sideOffset={12}
          collisionPadding={8}
          onOpenAutoFocus={(e) => {
            if (openedViaInput) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (wrapperRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between px-3 pt-3 -mb-2">
              <button
                type="button"
                className="text-sm text-primary underline cursor-pointer hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleTodayClick}
                disabled={isTodayDisabled}
              >
                Aujourd&apos;hui
              </button>
              {selectedDate && (
                <button
                  type="button"
                  onClick={clearValueAndClose}
                  className="text-sm text-red-500 underline cursor-pointer hover:text-red-600"
                >
                  Effacer
                </button>
              )}
            </div>
            <Calendar
              mode="single"
              captionLayout="dropdown"
              startMonth={new Date(new Date().getFullYear() - 100, 0)}
              endMonth={new Date(new Date().getFullYear() + 10, 11)}
              selected={selectedDate}
              onSelect={handleDateSelect}
              timeZone={APP_CONFIG.TIMEZONE}
              disabled={getDisabledDates}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              autoFocus={!openedViaInput}
              locale={fr}
            />
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal bg-white",
            !value && "text-muted-foreground",
            className,
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {getDisplayText()}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto"
        align="start"
        side="bottom"
        collisionPadding={8}
      >
        <div className="space-y-3">
          <button
            type="button"
            className="text-sm text-primary underline cursor-pointer hover:text-primary/80 px-3 pt-3 -mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleTodayClick}
            disabled={isTodayDisabled}
          >
            Aujourd&apos;hui
          </button>
          <Calendar
            mode="single"
            captionLayout="dropdown"
            startMonth={new Date(new Date().getFullYear() - 100, 0)}
            endMonth={new Date(new Date().getFullYear() + 10, 11)}
            selected={selectedDate}
            onSelect={handleDateSelect}
            timeZone={APP_CONFIG.TIMEZONE}
            disabled={getDisabledDates}
            defaultMonth={selectedDate}
            autoFocus
            locale={fr}
          />

          {showTime && (
            <div className="p-3 space-y-3">
              <div className="flex gap-2">
                <Input
                  type="time"
                  value={timeValue}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  min={minTime}
                  className="flex-1"
                  placeholder="HH:MM"
                />
              </div>

              {showTimeSelector && (
                <TimeSelector
                  selectedDate={selectedDate}
                  timeValue={timeValue}
                  onTimeChange={handleTimeChange}
                  minuteStep={minuteStep}
                  minHour={minHour}
                  maxHour={maxHour}
                />
              )}

              {showActions && (
                <DateTimeActions
                  onClear={clearValueAndClose}
                  onConfirm={() => setOpen(false)}
                  canConfirm={!!selectedDate && !!timeValue}
                />
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
